import { UzAlert, UzButton } from '@utazzo/ui';
import {
  ArrowRightLeft,
  BedDouble,
  Calendar,
  Check,
  Loader2,
  MapPin,
  Plane,
  Plus,
  RefreshCw,
  Ship,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { legForDate, stayHotelSource } from '../stay-rooms';
import {
  useAutoSearchItinerary,
  useAutoSearchPreview,
  useDeleteItineraryDay,
  useItineraryComponents,
  useItineraryDays,
  useItineraryDetail,
  useItineraryFlightPlan,
  useReconcileItinerary,
  useReconcileItineraryFlights,
  useSaveItineraryComponentsDiff,
  useUpdateItineraryComponentPatch,
} from '../use-itineraries';

import {
  ComponentCard,
  isFdBlockFlight,
  isFdLandPackage,
  isFdSupplement,
  TravelCard,
} from './ComponentCard';
import { ComponentFormDrawer } from './ComponentFormDrawer';
import { CruiseChangeModal } from './CruiseChangeModal';
import { DayItemPickerModal } from './DayItemPickerModal';
import { DaySlots } from './DaySlots';
import {
  applyPatch,
  draftToPayload,
  isComponentChanged,
  isLocalId,
  payloadToDraft,
} from './itinerary-draft';
import { PreferredHotelPickerModal } from './PreferredHotelPickerModal';
import { StayRoomsControl } from './StayRoomsControl';
import { TboPickerModal } from './TboPickerModal';

import type {
  CreateItineraryComponentRequest,
  ItineraryComponentOut,
  ItineraryDayOut,
  TransferCategory,
} from '@utazzo/shared';

/**
 * Days tab (B5.D): the day-by-day spine of an itinerary. Add / edit / remove
 * days; each card shows the components booked on it (read-only here - the
 * component picker lands in B5.E). Days are shown in trip order.
 */

/** How long to wait after the last edit before autosaving. Long enough to
 *  coalesce a burst of edits into one batched save, short enough that work is
 *  rarely at risk. */
const AUTOSAVE_DEBOUNCE_MS = 1500;

// The "already reconciled against this trip shape" signature has to survive
// a DaysTab remount, not just re-renders: switching to another step (e.g.
// Pricing/Preview) and back to Days fully unmounts + remounts this component
// (the step switch in ItineraryDetailPage renders a different case, no shared
// key), which would otherwise reset `reconciledSigRef` to null and make the
// structure-reconcile effect below fire again for a trip shape it already
// settled - re-running reconcile's gap-fill purely because the tab
// remounted, not because anything actually changed. A component the operator
// deliberately removed (hotel OR cruise) still reads as a "gap" to the
// server's reconcile (it has no way to tell "never filled" apart from
// "removed on purpose" - see auto-search.service.ts analyzeGaps), so a
// needless refire silently re-adds whatever it can find - most visibly a
// cruise, whose stay-fill (buildCruiseStayProposal) never returns "no
// proposal" the way a hotel search can. Caching the signature per itinerary
// id at module scope (outside the component) lets it survive the remount
// while still resetting for a different itinerary or a fresh page load.
const reconciledSigCache = new Map<string, string>();

// Slots (HOTEL / CRUISE) the operator deliberately emptied this session. The
// server's reconcile/analyzeGaps cannot tell "never filled" from "removed on
// purpose" - it sees a stay with no covering component as a gap and refills it
// (see auto-search.service.ts analyzeGaps + buildCruiseStayProposal, which for
// a cruise ALWAYS returns a proposal, so a reconcile firing for ANY reason -
// tab remount, an unrelated structure change, occupancy re-price - would
// deterministically re-add a just-removed cruise). We remember the operator's
// intent client-side, keyed per itinerary at module scope so it survives a
// DaysTab remount (switching steps and back), and filter it out of every
// reconcile/auto-fill proposal until the operator explicitly re-adds that slot.
// A fresh page load starts empty (new session), so first-load behaviour is
// unchanged. Cleared for a slot on an explicit re-add (see `addLocal`).
const removedAccommodationSlots = new Map<string, Set<string>>();
const getRemovedSlots = (itineraryId: string): Set<string> => {
  let set = removedAccommodationSlots.get(itineraryId);
  if (!set) {
    set = new Set<string>();
    removedAccommodationSlots.set(itineraryId, set);
  }
  return set;
};
// Identity of an accommodation slot: kind + city + start date. Both a removed
// component and a reconcile proposal resolve to the same key (same day -> same
// destination city, same check-in), so a removed slot filters out its own
// refill regardless of which side computes it.
const accommodationSlotKey = (
  kind: string,
  cityId: string | null,
  startDate: string | null,
): string => `${kind}|${cityId ?? ''}|${startDate ?? ''}`;

const formatDayDate = (iso: string | null): string | null => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

interface DaysTabProps {
  itineraryId: string;
  /** Whether the itinerary is still editable (DRAFT/IN_PROGRESS). */
  editable: boolean;
  /**
   * Fixed departures (ADR-0069): the day plan, hotels, activities and
   * transfers all come from the template, and the land price was quoted for
   * exactly that shape. Structure is therefore read-only for life - the agent
   * changes tier / dates / party on the Configure step, which re-seeds.
   */
  structureLocked?: boolean;
  /** Reports unsaved draft edits so the page can guard navigation. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Reports the debounced autosave's in-flight state so the parent page's
   *  top bar can render a "Saving..." pill instead of the tab rendering
   *  its own footer indicator. */
  onSavingChange?: (saving: boolean) => void;
  /** Set by the parent when Configure saved an edit; forces a hotel re-price +
   *  reconcile on mount so a dates/rooms/ages change made off this tab flows
   *  through even though the tab remounted (baseline would otherwise re-seed). */
  forceReprice?: boolean;
  /** Called once the forced re-price has been kicked off, to clear the flag. */
  onRepriceHandled?: () => void;
}

export const DaysTab = ({
  itineraryId,
  editable,
  structureLocked = false,
  onDirtyChange,
  onSavingChange,
  forceReprice = false,
  onRepriceHandled,
}: DaysTabProps): React.JSX.Element => {
  const daysQuery = useItineraryDays(itineraryId);
  const componentsQuery = useItineraryComponents(itineraryId);
  const detail = useItineraryDetail(itineraryId).data ?? null;
  // Server-authoritative journey shape (round-trip bundle vs one-way), derived
  // from the configured legs - NOT from a per-component flag. Drives how the
  // flight picker opens so international return trips are always bundled.
  const flightPlan = useItineraryFlightPlan(itineraryId).data ?? null;
  // Which component types are enabled. null configTypes = all enabled (the
  // pre-feature default, so existing itineraries are unaffected). A disabled
  // type gets no placeholder / no auto-fill / no price refresh here; the server
  // reconcile removes its placed components.
  const flightsEnabled = detail?.configTypes?.flights ?? true;
  const hotelsEnabled = detail?.configTypes?.hotels ?? true;
  const cruiseEnabled = detail?.configTypes?.cruise ?? true;
  const deleteDay = useDeleteItineraryDay(itineraryId);
  const saveComponents = useSaveItineraryComponentsDiff(itineraryId);
  const patchComponent = useUpdateItineraryComponentPatch(itineraryId);
  const auto = useAutoSearchItinerary(itineraryId);
  const reconcile = useReconcileItinerary(itineraryId);
  const reconcileFlights = useReconcileItineraryFlights(itineraryId);

  // Handler for the long-haul inline net-price editor on ACTIVITY / TRANSFER
  // rows. Stamps INR on the metaJson so the server's FX no-ops (agent enters
  // rupees). Awaited so the InlineNetPriceEditor's spinner reflects real
  // commit state.
  const handleManualPriceSave = async (
    componentId: string,
    newCostPaise: string,
  ): Promise<void> => {
    // Merge into the existing metaJson - a bare `{ currencyCode: 'INR' }` would
    // REPLACE it and wipe markers the day board relies on (`slot` for the
    // structural a2h/h2a/c2c placement, plus `transferMode` / `fareClassName` /
    // pax etc.), which would kick the component out of its slot. We only need to
    // pin the currency to INR so the server FX no-ops on the manual price.
    const existingMeta =
      (draftRef.current.find((c) => c.id === componentId)?.metaJson as Record<
        string,
        unknown
      > | null) ?? {};
    await patchComponent.mutateAsync({
      componentId,
      payload: {
        costPaise: newCostPaise,
        metaJson: { ...existingMeta, currencyCode: 'INR' },
      },
    });
  };
  const priceRefresh = useAutoSearchPreview(itineraryId);
  const autoTriggered = useRef(false);
  // The trip shape we last reconciled against; re-run only when it changes.
  // Seeded from the module-level cache so a remount of this tab (switching
  // steps and back) doesn't re-run reconcile for a shape already settled -
  // see `reconciledSigCache` above.
  const reconciledSigRef = useRef<string | null>(reconciledSigCache.get(itineraryId) ?? null);
  // Same idea for the FD flights-only auto-fetch (independent of the generic
  // reconcile, which is disabled for fixed departures).
  const fdFlightSigRef = useRef<string | null>(null);

  // Local working copy of the components. Edits stay here until they are
  // persisted - either by the debounced autosave or the manual Save button -
  // which diffs the draft against the server list and saves only the delta.
  const [draft, setDraft] = useState<ItineraryComponentOut[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [componentDrawer, setComponentDrawer] = useState<{
    mode: 'create' | 'edit';
    dayId: string | null;
    component?: ItineraryComponentOut;
  } | null>(null);
  const [tboDrawer, setTboDrawer] = useState<{
    kind: 'hotel' | 'flight';
    dayId: string | null;
    cityId: string | null;
    cityName: string | null;
    dayDate: string | null;
    originCityId: string | null;
    /** Pre-resolved From/To IATA (from the flight component being changed), so
     *  the picker prefills the route without a city-id -> getCity round trip. */
    originIata?: string | null;
    destIata?: string | null;
    /** When set, picking a result REPLACES this draft component (swap). */
    replaceId?: string | null;
    /** The currently-selected option being edited - drives the "Selected"
     *  badge + price delta (+/-) shown against each result. */
    currentResultIndex?: string | null;
    currentCostPaise?: string | null;
    /** Round-trip (bundle) edit: both legs picked together as one joint fare.
     *  Set when editing a bundled flight; replaces BOTH components on pick. */
    bundle?: {
      outboundOrigin: string;
      outboundDestination: string;
      outboundDate: string;
      returnOrigin: string;
      returnDestination: string;
      returnDate: string;
      currentResultIndexes: string[];
      // Null when that direction has no component yet (first pick) - the bundle
      // submit then ADDS it instead of replacing.
      outboundReplaceId: string | null;
      returnReplaceId: string | null;
    } | null;
  } | null>(null);
  // Preferred (in-house) hotel picker. Used instead of the TBO picker when the
  // itinerary's hotelSource is PREFERRED. Same replace/add semantics.
  const [prefHotelDrawer, setPrefHotelDrawer] = useState<{
    dayId: string | null;
    cityId: string | null;
    cityName: string | null;
    dayDate: string | null;
    legNights: number | null;
    replaceId?: string | null;
  } | null>(null);
  // "Change cruise" / "Change cabin & meals" picker for a placed CRUISE stay.
  // Mirrors prefHotelDrawer: dayId + embark cityId/checkIn/nights derived off
  // the day the CRUISE component sits on, replaceId swaps it in place.
  const [cruiseDrawer, setCruiseDrawer] = useState<{
    dayId: string | null;
    cityId: string | null;
    cityName: string | null;
    checkIn: string | null;
    nights: number;
    currency: string;
    replaceId?: string | null;
    // Set to the current cruise's id for "Change cabin & meals" (locks the
    // modal to that one cruise); null for "Change cruise" (full list).
    lockedCruiseId?: string | null;
  } | null>(null);
  const [itemPicker, setItemPicker] = useState<{
    dayId: string;
    cityId: string | null;
    cityName: string | null;
    defaultTime: string;
    initialTab?: 'ACTIVITY' | 'TRANSFER';
    initialCategory?: TransferCategory;
    /** Structural-transfer slot marker stored on the added component. */
    slot?: 'a2h' | 'h2a' | 'c2c';
    /**
     * 2026-07-07. When set, the picker acts as a "change transfer" flow: the
     * existing draft component is removed and the new picked one takes its
     * sortOrder/slot. Wired from the edit-icon on structural transfer cards
     * so operators browse the transfer catalog instead of the generic
     * component-edit drawer.
     */
    replaceId?: string;
    /** Cost of the component being replaced — drives +/- deltas for non-admins. */
    currentCostPaise?: string | null;
  } | null>(null);

  const days = daysQuery.data ?? [];
  const serverComponents = componentsQuery.data ?? [];
  // The editor renders the DRAFT, not the server list directly.
  const components = draft;

  // Refs let the debounced autosave read the latest draft/server lists without
  // re-arming on every render, and detect edits that land mid-save (so we don't
  // clear "dirty" over them). Kept in sync on each render below.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const serverComponentsRef = useRef(serverComponents);
  serverComponentsRef.current = serverComponents;
  // Lets the async reconcile closures resolve a day's city without re-arming on
  // every render (the effects don't depend on `days`, so a bare closure capture
  // would go stale).
  const daysRef = useRef(days);
  daysRef.current = days;
  // A day maps 1:1 to its destination city; an accommodation slot is keyed by
  // that city (+ kind + start date), so a removed component and its reconcile
  // refill proposal - both anchored to the same day - produce the same slot key.
  const cityIdOfDay = (dayId: string | null): string | null =>
    dayId ? (daysRef.current.find((d) => d.id === dayId)?.destinationCityId ?? null) : null;
  // Monotonic counter bumped on every local edit; a save only clears `dirty`
  // if no new edit arrived while it was in flight.
  const editSeqRef = useRef(0);
  const persistRef = useRef<(opts?: { silent?: boolean }) => Promise<void>>(async () => {});

  const markDirty = (): void => {
    editSeqRef.current += 1;
    setDirty(true);
  };

  // Adopt the server list into the draft whenever it changes and there are no
  // unsaved edits (initial load, after Save, after auto-fill).
  useEffect(() => {
    if (componentsQuery.isSuccess && !dirty) {
      setDraft(componentsQuery.data ?? []);
    }
  }, [componentsQuery.isSuccess, componentsQuery.data, dirty]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);
  // Assign each configured flight leg to the day matching its departure date
  // (fallback: the first day). This is the single source of truth for where a
  // flight sits + what its "Add flight" search uses (exact from -> to -> date).
  const flightLegByDayId = useMemo(() => {
    const m = new Map<string, NonNullable<typeof detail>['flightLegs'][number]>();
    for (const fl of detail?.flightLegs ?? []) {
      const target =
        (fl.departureDate ? days.find((d) => d.date === fl.departureDate) : null) ?? days[0];
      if (target && !m.has(target.id)) m.set(target.id, fl);
    }
    return m;
  }, [detail?.flightLegs, days]);
  const pending = deleteDay.isPending;
  const componentPending = saving || saveComponents.isPending;
  // While the initial auto-fill or a reconcile is fetching + auto-filling
  // flights/hotels, lock ALL day-by-day editing so the agent can't add/remove
  // items into a plan that's mid-search (which would conflict with the results
  // being written in). Everything unlocks once the search settles.
  const autoBusy =
    auto.isPending || reconcile.isPending || reconcileFlights.isPending || priceRefresh.isPending;
  const canEdit = editable && !autoBusy;

  // On first landing on a freshly-generated plan (days exist, no components yet),
  // fetch the cheapest hotel per city + flight per flown leg. Runs once per mount;
  // the server is non-destructive so it never clobbers an itinerary with items.
  useEffect(() => {
    if (autoTriggered.current || !editable) return;
    // A fixed departure's plan (days, hotels, land package) is seeded from the
    // template and is structurally locked - never auto-fill or reconcile it, or
    // the reconcile tries to delete template components and the FD lock rejects
    // the delete in a loop.
    if (structureLocked) return;
    if (!daysQuery.isSuccess || !componentsQuery.isSuccess) return;
    // Only ever runs on a FULLY empty plan (zero components) - a fresh
    // generate/regenerate, which is a legitimate full fill. It is NOT gated on
    // the removed-slot set: a single removal never empties a multi-component
    // plan (so this doesn't run for the reported flow - the reconcile `toAdd`
    // filter below handles that), and after a Configure regenerate the operator
    // DOES want the new plan filled. The stale removal intents are cleared when
    // structureSig changes (see the reconcile effect below).
    if ((daysQuery.data?.length ?? 0) > 0 && (componentsQuery.data?.length ?? 0) === 0) {
      autoTriggered.current = true;
      auto.mutate();
    }
  }, [
    daysQuery.isSuccess,
    componentsQuery.isSuccess,
    daysQuery.data,
    componentsQuery.data,
    editable,
    structureLocked,
    auto,
  ]);

  // Signature of the current trip shape (cities, nights, flight legs, dates,
  // cruise stays). When it changes vs what the existing components were
  // built for, the plan needs reconciling.
  const structureSig = useMemo(() => {
    const legs = [...(detail?.legs ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    // Cruise stays (cruise-as-stay Phase 2) are a trip-shape input exactly
    // like a hotel leg - adding/removing/editing one in Configure must
    // reconcile the day board (fill/drop the CRUISE component) the same way
    // a city leg change does. Without this, editing a cruise stay leaves
    // `structureSig` unchanged, so the reconcile effect below never fires for
    // that change.
    const cruiseStays = [...(detail?.cruiseStays ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    return [
      detail?.travelStartDate ?? '',
      detail?.returnFlightDate ?? '',
      ...legs.map((l) => `${l.cityId}:${l.nights}`),
      // Flight config drives the flight search - a change here must reconcile.
      '#',
      ...(detail?.flightLegs ?? []).map(
        (f) => `${f.fromCityId}>${f.toCityId}@${f.departureDate ?? ''}`,
      ),
      '#',
      ...cruiseStays.map((cs) => `${cs.cityId}@${cs.embarkDate}:${cs.nights}`),
      // Enabling/disabling a type is a structure change - toggling it must
      // reconcile (remove/re-add that type's components) on the next save.
      `#cfg:${flightsEnabled}:${hotelsEnabled}:${cruiseEnabled}`,
    ].join('|');
  }, [detail, flightsEnabled, hotelsEnabled, cruiseEnabled]);

  // Copy-forward versions (v2+ created via "Copy current version") arrive with a
  // complete plan. Seed the reconcile guard on first landing so we don't fire a
  // live TBO search that silently re-prices flights/hotels before publish.
  useEffect(() => {
    if (!detail?.parentItineraryId || !structureSig) return;
    if (reconciledSigRef.current === null) {
      reconciledSigRef.current = structureSig;
      reconciledSigCache.set(itineraryId, structureSig);
    }
  }, [detail?.parentItineraryId, structureSig, itineraryId]);

  // After the trip shape changes (cities added/removed/reordered, dates moved),
  // reconcile the day-by-day: add new-city hotels + internal flights and
  // re-search the round-trip when its endpoints/dates changed, dropping only
  // stale/orphan components and keeping everything still valid (manual picks).
  // Runs once per distinct trip shape, against a clean (saved) draft.
  useEffect(() => {
    if (!editable || !detail) return;
    // Fixed departures are structurally locked - the template owns the days,
    // hotels and land price. The generic reconcile flags template components as
    // stale and its autosave churn destabilised the flight-search modal, so it
    // is disabled here. FD flights are added/searched by hand on this tab.
    if (structureLocked) return;
    if (!daysQuery.isSuccess || !componentsQuery.isSuccess) return;
    if (draft.length === 0) return; // empty plan -> the initial auto-fill owns it
    if (dirty || saving) return; // only reconcile a clean draft == server
    if (auto.isPending || reconcile.isPending) return;
    if (reconciledSigRef.current === structureSig) return; // already in sync

    // The trip shape genuinely changed (Configure edit / regenerate): prior
    // "operator removed this slot" intents are now stale - the slots they
    // referred to may not even exist anymore, and a fresh generate is a
    // legitimate full refill. Clear them so a remove-then-regenerate fills the
    // new plan normally. (Removing just a COMPONENT never reaches here: the
    // stay persists, so structureSig is unchanged and this branch is skipped.)
    removedAccommodationSlots.delete(itineraryId);

    reconciledSigRef.current = structureSig;
    reconciledSigCache.set(itineraryId, structureSig);
    const runReconcile = reconcile.mutateAsync;
    void (async () => {
      try {
        const { toAdd, toRemoveIds } = await runReconcile();
        // Never re-add a hotel/cruise slot the operator deliberately emptied
        // this session - the server has no memory of the removal and would
        // otherwise refill it (a cruise always, a hotel sometimes).
        const removedSlots = getRemovedSlots(itineraryId);
        const addable = toAdd.filter((p) => {
          if (p.component.kind !== 'HOTEL' && p.component.kind !== 'CRUISE') return true;
          return !removedSlots.has(
            accommodationSlotKey(
              p.component.kind,
              cityIdOfDay(p.dayId),
              p.component.startDate ?? null,
            ),
          );
        });
        if (addable.length === 0 && toRemoveIds.length === 0) return; // nothing stale
        const removeSet = new Set(toRemoveIds);
        setDraft((d) => [
          ...d.filter((c) => !removeSet.has(c.id)),
          ...addable.map((p) => payloadToDraft(p.component, p.dayId, itineraryId)),
        ]);
        editSeqRef.current += 1;
        setDirty(true); // autosave persists the reconciled plan
        const addN = addable.length;
        toast.success(
          addN > 0
            ? `Updated flights & hotels for your trip changes (${addN} added)`
            : 'Removed flights & hotels that no longer fit your trip',
        );
      } catch {
        // Best-effort: a failed reconcile must never block the editor or
        // retry-storm. The guard stays set for this trip shape, so we don't
        // re-attempt until the shape changes again - the operator can edit
        // Configure to re-run the trip generation.
      }
    })();
  }, [
    editable,
    detail,
    structureLocked,
    daysQuery.isSuccess,
    componentsQuery.isSuccess,
    draft.length,
    dirty,
    saving,
    auto.isPending,
    reconcile.isPending,
    reconcile.mutateAsync,
    structureSig,
    itineraryId,
  ]);

  // Fixed departure in LIVE mode: the generic reconcile above is disabled (it
  // would churn the template hotels/land package). But the agent's configured
  // flight legs still need their live fares fetched - so run a FLIGHT-ONLY
  // reconcile once per trip shape, staging the fares into the draft and never
  // touching hotels/land. This is what makes "switch to Live -> open Days ->
  // flights auto-fill" work the way FIT does. BLOCK mode owns its own flights,
  // so it is skipped.
  useEffect(() => {
    if (!editable || !detail) return;
    if (!structureLocked) return; // fixed departures only
    if (detail.fdFlightMode !== 'LIVE') return; // BLOCK attaches its own flights
    if (!daysQuery.isSuccess || !componentsQuery.isSuccess) return;
    if ((detail.flightLegs?.length ?? 0) === 0) return; // no legs -> nothing to fetch
    if (dirty || saving) return; // only against a clean draft == server
    if (auto.isPending || reconcileFlights.isPending) return;
    if (fdFlightSigRef.current === structureSig) return; // already fetched for this shape

    fdFlightSigRef.current = structureSig;
    const run = reconcileFlights.mutateAsync;
    void (async () => {
      try {
        const { toAdd, toRemoveIds } = await run();
        const removeSet = new Set(toRemoveIds);
        const flightAdds = toAdd.filter((p) => p.component.kind === 'FLIGHT');
        if (flightAdds.length === 0 && removeSet.size === 0) return;
        setDraft((d) => [
          ...d.filter((c) => !removeSet.has(c.id)),
          ...flightAdds.map((p) => payloadToDraft(p.component, p.dayId, itineraryId)),
        ]);
        editSeqRef.current += 1;
        setDirty(true); // autosave persists the fetched fares
        if (flightAdds.length > 0)
          toast.success(
            `Fetched ${flightAdds.length} live flight${flightAdds.length === 1 ? '' : 's'}`,
          );
      } catch {
        // Best-effort: never block the editor. The guard stays set for this
        // shape, so a failure isn't retried until the config changes again.
      }
    })();
  }, [
    editable,
    detail,
    structureLocked,
    daysQuery.isSuccess,
    componentsQuery.isSuccess,
    dirty,
    saving,
    auto.isPending,
    reconcileFlights.isPending,
    reconcileFlights.mutateAsync,
    structureSig,
    itineraryId,
  ]);

  // ---- Local draft mutators (nothing persists until Save) ----
  const addLocal = (dayId: string | null, payload: CreateItineraryComponentRequest): void => {
    // An explicit re-add of a hotel/cruise clears the "operator removed this"
    // intent for that slot, so future reconciles are free to manage it again.
    if (payload.kind === 'HOTEL' || payload.kind === 'CRUISE') {
      getRemovedSlots(itineraryId).delete(
        accommodationSlotKey(payload.kind, cityIdOfDay(dayId), payload.startDate ?? null),
      );
    }
    setDraft((d) => {
      // Append at the end of this day so DaySlots' drag-to-reorder keeps
      // a stable, monotonically increasing sortOrder per day.
      const dayMax = d
        .filter((c) => c.dayId === dayId)
        .reduce((m, c) => Math.max(m, c.sortOrder), -1);
      const nextOrder = payload.sortOrder ?? dayMax + 1;
      return [...d, payloadToDraft({ ...payload, sortOrder: nextOrder }, dayId, itineraryId)];
    });
    markDirty();
  };
  const removeLocal = (id: string): void => {
    setDraft((d) => d.filter((c) => c.id !== id));
    markDirty();
  };
  const updateLocal = (id: string, patch: Partial<CreateItineraryComponentRequest>): void => {
    setDraft((d) => d.map((c) => (c.id === id ? applyPatch(c, patch) : c)));
    markDirty();
  };
  /**
   * Reassign sortOrder on a day's free items after a drag-to-reorder. Other
   * days are left alone; structural transfers + flight + hotel keep their
   * original sortOrder (they aren't draggable in this list). The autosave
   * diff picks up the changed sortOrders and patches them server-side.
   */
  const reorderLocal = (dayId: string, orderedIds: string[]): void => {
    const orderIdx = new Map(orderedIds.map((id, i) => [id, i]));
    setDraft((d) =>
      d.map((c) => {
        if (c.dayId !== dayId) return c;
        const i = orderIdx.get(c.id);
        if (i === undefined) return c;
        return c.sortOrder === i ? c : { ...c, sortOrder: i };
      }),
    );
    markDirty();
  };
  /** Swap a flight/hotel for a freshly picked one, keeping its id + day. */
  const replaceLocal = (id: string, payload: CreateItineraryComponentRequest): void => {
    setDraft((d) =>
      d.map((c) =>
        c.id === id ? { ...payloadToDraft(payload, c.dayId, itineraryId), id: c.id } : c,
      ),
    );
    markDirty();
  };

  const submitComponent = async (payload: CreateItineraryComponentRequest): Promise<void> => {
    if (componentDrawer?.mode === 'edit' && componentDrawer.component) {
      updateLocal(componentDrawer.component.id, payload);
    } else {
      addLocal(componentDrawer?.dayId ?? null, payload);
    }
    setComponentDrawer(null);
  };

  // The search modal either swaps a targeted component (single pick, then close)
  // or adds to the open day (stays open so several can be added in a row).
  const submitTboComponent = async (payload: CreateItineraryComponentRequest): Promise<void> => {
    if (tboDrawer?.replaceId) {
      replaceLocal(tboDrawer.replaceId, payload);
      setTboDrawer(null);
    } else {
      addLocal(tboDrawer?.dayId ?? null, payload);
      // A flight is a single pick per leg, so close the picker once one is added
      // (the "stays open to add several" behaviour is only meaningful for hotels,
      // where several can share a day). Applies to FIT and FD alike - closing on
      // a flight pick doesn't affect FIT pricing or flow.
      if (payload.kind === 'FLIGHT') setTboDrawer(null);
    }
  };

  // Preferred hotel pick: same swap-or-add behaviour as the TBO picker, but
  // driven by the in-house hotel modal's drawer context.
  const submitPrefHotel = async (payload: CreateItineraryComponentRequest): Promise<void> => {
    if (prefHotelDrawer?.replaceId) {
      replaceLocal(prefHotelDrawer.replaceId, payload);
    } else {
      addLocal(prefHotelDrawer?.dayId ?? null, payload);
    }
    setPrefHotelDrawer(null);
  };

  // Cruise cabin pick: always a swap-in-place - the day board only opens this
  // picker from an existing CRUISE component's "Change cruise" / "Change
  // cabin & meals" actions, so replaceId is always set. replaceLocal keeps
  // the component's id, so a later re-generate's keep-in-place still matches
  // (see loadExistingCruiseByStay in auto-search.service.ts).
  const submitCruise = async (payload: CreateItineraryComponentRequest): Promise<void> => {
    if (cruiseDrawer?.replaceId) {
      replaceLocal(cruiseDrawer.replaceId, payload);
    } else {
      addLocal(cruiseDrawer?.dayId ?? null, payload);
    }
    setCruiseDrawer(null);
  };

  // Round-trip pick: a single joint fare updates BOTH the outbound and the
  // return legs at once (they stay one bookable set). A direction with no
  // existing component (null replace id, e.g. first pick on an international
  // return trip) is ADDED rather than replaced.
  const submitTboBundle = async (
    outbound: CreateItineraryComponentRequest,
    ret: CreateItineraryComponentRequest,
  ): Promise<void> => {
    const b = tboDrawer?.bundle;
    if (b) {
      const dayId = tboDrawer?.dayId ?? null;
      if (b.outboundReplaceId) replaceLocal(b.outboundReplaceId, outbound);
      else addLocal(dayId, outbound);
      if (b.returnReplaceId) replaceLocal(b.returnReplaceId, ret);
      else addLocal(dayId, ret);
    }
    setTboDrawer(null);
  };

  // Server-authoritative bundle gate. If the trip is an international round trip
  // and this leg is the outbound or the return, open the picker in JOINT
  // round-trip mode (searching + replacing BOTH legs as one fare) regardless of
  // any stored per-component flag - this is what fixes an international leg
  // being (re-)picked one-way. Returns true when it opened the bundle picker;
  // callers fall through to the one-way picker when it returns false.
  const tryOpenFlightBundle = (
    dayId: string,
    leg: {
      fromIata?: string | null;
      toIata?: string | null;
      fromCityId?: string | null;
      toCityId?: string | null;
    },
  ): boolean => {
    // RETURN (true round trip) and MULTICITY (open-jaw, e.g. Delhi->Denpasar
    // ... DaNang->Delhi) both bundle the first + last legs into one joint fare;
    // only ONEWAY / domestic skip it.
    const bundle = flightPlan && flightPlan.shape !== 'ONEWAY' ? flightPlan.bundle : null;
    if (!bundle) return false;
    const norm = (v: string | null | undefined): string => (v ?? '').trim().toUpperCase();
    const matchesLeg = (
      fromIata: string,
      toIata: string,
      fromCityId: string | null,
      toCityId: string | null,
    ): boolean => {
      if (leg.fromIata && leg.toIata)
        return norm(leg.fromIata) === norm(fromIata) && norm(leg.toIata) === norm(toIata);
      if (leg.fromCityId && leg.toCityId)
        return leg.fromCityId === fromCityId && leg.toCityId === toCityId;
      return false;
    };
    const isOutbound = matchesLeg(
      bundle.outboundOrigin,
      bundle.outboundDestination,
      bundle.outboundFromCityId,
      bundle.outboundToCityId,
    );
    const isReturn = matchesLeg(
      bundle.returnOrigin,
      bundle.returnDestination,
      bundle.returnFromCityId,
      bundle.returnToCityId,
    );
    if (!isOutbound && !isReturn) return false;
    // Resolve the current outbound/return FLIGHT components by route (IATA), so
    // the pick replaces them even if they were saved as unbundled one-ways.
    const findByRoute = (origin: string, dest: string): ItineraryComponentOut | undefined =>
      draft.find(
        (c) =>
          c.kind === 'FLIGHT' &&
          norm(c.fromLocation) === norm(origin) &&
          norm(c.toLocation) === norm(dest),
      );
    const out = findByRoute(bundle.outboundOrigin, bundle.outboundDestination);
    const ret = findByRoute(bundle.returnOrigin, bundle.returnDestination);
    setTboDrawer({
      kind: 'flight',
      dayId,
      // The outbound's cities, so the picker can offer each one's
      // neighbouring airports (the return is the same pair reversed).
      cityId: bundle.outboundToCityId ?? null,
      cityName: null,
      dayDate: null,
      originCityId: bundle.outboundFromCityId ?? null,
      currentCostPaise: out?.costPaise ?? null,
      bundle: {
        outboundOrigin: bundle.outboundOrigin,
        outboundDestination: bundle.outboundDestination,
        outboundDate: bundle.outboundDate,
        returnOrigin: bundle.returnOrigin,
        returnDestination: bundle.returnDestination,
        returnDate: bundle.returnDate,
        currentResultIndexes: [
          (out?.metaJson as { resultIndex?: string } | null)?.resultIndex ?? '',
          (ret?.metaJson as { resultIndex?: string } | null)?.resultIndex ?? '',
        ],
        outboundReplaceId: out?.id ?? null,
        returnReplaceId: ret?.id ?? null,
      },
    });
    return true;
  };

  const removeComponent = (c: ItineraryComponentOut): void => {
    // Record a deliberately-removed hotel/cruise so a later reconcile/auto-fill
    // never resurrects it (the server treats the still-present stay as a gap and
    // always re-proposes a cruise). "Remove just remove" - symmetric for both.
    if (c.kind === 'HOTEL' || c.kind === 'CRUISE') {
      getRemovedSlots(itineraryId).add(
        accommodationSlotKey(c.kind, cityIdOfDay(c.dayId), c.startDate),
      );
    }
    removeLocal(c.id);
  };

  const findPriceRefreshTarget = (
    components: ItineraryComponentOut[],
    proposal: { dayId: string; component: CreateItineraryComponentRequest },
  ): ItineraryComponentOut | undefined => {
    const { dayId, component } = proposal;
    if (component.kind !== 'FLIGHT' && component.kind !== 'HOTEL' && component.kind !== 'CRUISE')
      return undefined;
    const sameDay = components.filter((c) => c.dayId === dayId && c.kind === component.kind);
    if (sameDay.length === 0) return undefined;
    if (component.kind === 'FLIGHT' && component.fromLocation && component.toLocation) {
      const routeMatch = sameDay.find(
        (c) =>
          c.fromLocation?.toUpperCase() === component.fromLocation?.toUpperCase() &&
          c.toLocation?.toUpperCase() === component.toLocation?.toUpperCase(),
      );
      if (routeMatch) return routeMatch;
    }
    return sameDay[0];
  };

  const handleFetchLatestPrices = async (
    opts: { flights: boolean; hotels: boolean; cruise: boolean } = {
      flights: flightsEnabled,
      hotels: hotelsEnabled,
      cruise: cruiseEnabled,
    },
  ): Promise<void> => {
    if (!editable || autoBusy || saving) return;
    try {
      const { proposals, removeComponentIds } = await priceRefresh.mutateAsync(opts);
      if (proposals.length === 0 && removeComponentIds.length === 0) return;

      const removeSet = new Set(removeComponentIds);
      let updated = 0;
      setDraft((current) => {
        // Blank hotels the server flagged unavailable, then patch re-priced rows.
        const next = current.filter((c) => !removeSet.has(c.id));
        updated += current.length - next.length;
        for (const proposal of proposals) {
          const target = findPriceRefreshTarget(next, proposal);
          if (!target) continue;
          const idx = next.findIndex((c) => c.id === target.id);
          if (idx < 0) continue;
          const row = next[idx];
          if (!row) continue;
          next[idx] = applyPatch(row, {
            title: proposal.component.title,
            source: proposal.component.source,
            tboHotelCode: proposal.component.tboHotelCode,
            costPaise: proposal.component.costPaise,
            startDate: proposal.component.startDate,
            endDate: proposal.component.endDate,
            fromLocation: proposal.component.fromLocation,
            toLocation: proposal.component.toLocation,
            isRefundable: proposal.component.isRefundable,
            metaJson: proposal.component.metaJson,
          });
          updated += 1;
        }
        return next;
      });

      if (updated === 0) return;

      editSeqRef.current += 1;
      setDirty(true);
    } catch {
      // Silent best-effort refresh; failures must not interrupt the editor.
    }
  };

  // Re-price STAYS (hotels + cruise cabins) when the room/cabin occupancy
  // changes (adults / children / child-no-bed / infants). A room-config edit in
  // Configure must flow through to the stay cost - in-house hotels and cruise
  // cabins re-run the pricing engine, TBO re-fetches - exactly like "Fetch latest
  // prices", just auto-fired on the occupancy change. Stays only (flights are
  // left to the explicit button / reconcile). Mirrors the reconcile guards: only
  // against a clean (saved) draft, never mid-edit.
  const occupancySig = useMemo(() => {
    const rooms = detail?.roomGroups?.flatMap((g) => g.rooms) ?? [];
    return rooms.length > 0
      ? rooms
          // Child ages are part of occupancy: TBO prices children by age band, so
          // an age change must re-price hotels just like an adults/children change.
          .map(
            (r) =>
              `${r.adults}.${r.children}.${r.childrenNoBed ?? 0}.${r.infants}.${(r.childAges ?? []).join(',')}`,
          )
          .join('|')
      : `${detail?.adultsCount ?? 0}.${detail?.childrenCount ?? 0}.${detail?.infantsCount ?? 0}`;
  }, [detail]);
  const occupancySigRef = useRef<string | null>(null);
  const fetchLatestRef = useRef(handleFetchLatestPrices);
  fetchLatestRef.current = handleFetchLatestPrices;
  useEffect(() => {
    if (!editable || !detail || draft.length === 0) return;
    // Configure just saved an edit (dates/rooms/ages). The tab remounted so the
    // baseline reseeds - the in-tab change detection can't see it. Force the
    // re-price once things are clean, then clear the flag.
    if (forceReprice) {
      if (dirty || saving || autoBusy) return;
      occupancySigRef.current = occupancySig;
      onRepriceHandled?.();
      void fetchLatestRef.current({
        flights: flightsEnabled,
        hotels: hotelsEnabled,
        cruise: cruiseEnabled,
      });
      return;
    }
    // Seed the baseline on first clean render - don't re-price on mount.
    if (occupancySigRef.current === null) {
      occupancySigRef.current = occupancySig;
      return;
    }
    if (occupancySigRef.current === occupancySig) return;
    // Occupancy changed in-tab - wait for a clean, idle draft, then re-price
    // hotels AND flights (fares scale with pax). Both keep the same product in
    // place and only swap when it's unavailable for the new occupancy.
    if (dirty || saving || autoBusy) return;
    occupancySigRef.current = occupancySig;
    void fetchLatestRef.current({
      flights: flightsEnabled,
      hotels: hotelsEnabled,
      cruise: cruiseEnabled,
    });
  }, [
    editable,
    detail,
    draft.length,
    dirty,
    saving,
    autoBusy,
    occupancySig,
    forceReprice,
    onRepriceHandled,
    flightsEnabled,
    hotelsEnabled,
    cruiseEnabled,
  ]);

  // ---- Save / Discard / Resync ----
  // Persist the draft -> server delta in one batched call. Reads the latest
  // draft/server lists from refs so the debounced autosave never saves a stale
  // snapshot. `silent` keeps autosave from toasting on every cycle.
  const persist = async (opts?: { silent?: boolean }): Promise<void> => {
    if (saving || saveComponents.isPending) return; // never overlap saves
    const seq = editSeqRef.current;
    const currentDraft = draftRef.current;
    const currentServer = serverComponentsRef.current;

    const draftIds = new Set(currentDraft.map((c) => c.id));
    const deletes = currentServer.filter((s) => !draftIds.has(s.id)).map((s) => s.id);
    const adds: {
      tmpId: string;
      dayId: string | null;
      payload: CreateItineraryComponentRequest;
    }[] = [];
    const updates: { componentId: string; payload: CreateItineraryComponentRequest }[] = [];
    for (const c of currentDraft) {
      if (isLocalId(c.id)) {
        adds.push({ tmpId: c.id, dayId: c.dayId, payload: draftToPayload(c) });
      } else {
        const server = currentServer.find((s) => s.id === c.id);
        if (server && isComponentChanged(c, server)) {
          updates.push({ componentId: c.id, payload: draftToPayload(c) });
        }
      }
    }

    // Nothing actually changed vs the server - just clear the flag.
    if (deletes.length === 0 && adds.length === 0 && updates.length === 0) {
      if (editSeqRef.current === seq) setDirty(false);
      return;
    }

    setSaving(true);
    try {
      const { createdIds } = await saveComponents.mutateAsync({ deletes, adds, updates });
      // Swap the tmp_ ids of just-created components for their real server ids
      // in the live draft, so a later save never re-adds them - even if the
      // agent kept editing during this save. Keyed by id, so concurrent edits
      // to other components are untouched.
      if (Object.keys(createdIds).length > 0) {
        setDraft((d) =>
          d.map((c) => {
            const realId = createdIds[c.id];
            return realId ? { ...c, id: realId } : c;
          }),
        );
      }
      setSavedAt(Date.now());
      // Only clear dirty if no edit landed while the save was in flight; if one
      // did, leave it dirty so autosave picks up the newer state next.
      if (editSeqRef.current === seq) setDirty(false);
      if (!opts?.silent) toast.success('Plan saved');
    } catch {
      toast.error('Some changes could not be saved; review and retry.');
    } finally {
      setSaving(false);
    }
  };
  persistRef.current = persist;

  // Debounced autosave: when there are unsaved edits and nothing else is
  // running, save once the agent pauses. The timer is cleared on every edit
  // (resetting the debounce) and on unmount (no dangling timer / no save after
  // the editor is gone).
  useEffect(() => {
    if (!editable || !dirty || saving) return;
    if (auto.isPending) return;
    if (priceRefresh.isPending) return;
    const t = setTimeout(() => {
      void persistRef.current({ silent: true });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [editable, dirty, saving, draft, auto.isPending, priceRefresh.isPending]);

  const byDay = useMemo(() => {
    const map = new Map<string, ItineraryComponentOut[]>();
    const unscheduled: ItineraryComponentOut[] = [];
    const supplements: ItineraryComponentOut[] = [];
    for (const c of components) {
      // The fixed-departure land package is a costed VAS that stands in for the
      // whole trip; it is kept in the draft (so its cost survives a save) but
      // hidden from the day plan, which reads as double-billing otherwise.
      if (isFdLandPackage(c)) continue;
      // Fixed-departure supplements get their own read-only section, not the
      // generic "Unscheduled" bucket.
      if (isFdSupplement(c)) {
        supplements.push(c);
        continue;
      }
      if (c.dayId) {
        const arr = map.get(c.dayId) ?? [];
        arr.push(c);
        map.set(c.dayId, arr);
      } else {
        unscheduled.push(c);
      }
    }
    return { map, unscheduled, supplements };
  }, [components]);

  const handleDelete = (day: ItineraryDayOut): void => {
    if ((byDay.map.get(day.id)?.length ?? 0) > 0) {
      // Backend blocks deleting a day with components; tell the user up front.
      window.alert('Remove the components on this day before deleting it.');
      return;
    }
    if (!window.confirm(`Remove day ${day.dayIndex}${day.title ? ` (${day.title})` : ''}?`)) return;
    deleteDay.mutate(day.id);
  };

  if (daysQuery.isError) {
    return <UzAlert tone="destructive">Failed to load days. Reload and retry.</UzAlert>;
  }

  return (
    <div className="pb-20">
      <div className="space-y-4">
        {auto.isPending && (
          <div className="border-primary/20 bg-accent text-primary sticky top-18 z-10 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-sm lg:top-16">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="font-medium">
              Fetching the best flights & hotels for your route...
            </span>
            <span className="text-primary/70">
              editing is paused until this finishes, then change any of them.
            </span>
          </div>
        )}

        {reconcile.isPending && !auto.isPending && (
          <div className="border-primary/20 bg-accent text-primary sticky top-18 z-10 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-sm lg:top-16">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="font-medium">Updating flights & hotels for your trip changes...</span>
            <span className="text-primary/70">
              editing is paused until this finishes - your picks are kept.
            </span>
          </div>
        )}

        {reconcileFlights.isPending && !auto.isPending && !reconcile.isPending && (
          <div className="border-primary/20 bg-accent text-primary sticky top-18 z-10 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-sm lg:top-16">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="font-medium">Fetching live flights for your route...</span>
            <span className="text-primary/70">editing is paused until this finishes.</span>
          </div>
        )}

        {priceRefresh.isPending && !auto.isPending && !reconcile.isPending && (
          <div className="border-primary/20 bg-accent text-primary sticky top-18 z-10 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-sm lg:top-16">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="font-medium">Fetching the latest flight & hotel prices...</span>
            <span className="text-primary/70">editing is paused until this finishes.</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="inline-flex items-center gap-2 text-sm text-zinc-500">
            {days.length === 0
              ? 'No days yet.'
              : `${days.length} day${days.length === 1 ? '' : 's'}`}
            {editable && !dirty && !saving && savedAt && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                <Check className="h-3 w-3" aria-hidden />
                All changes saved
              </span>
            )}
          </p>
          {canEdit && days.length > 0 && (
            <UzButton
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void handleFetchLatestPrices()}
              loading={priceRefresh.isPending}
              disabled={saving || componentPending}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
              Fetch latest prices
            </UzButton>
          )}
        </div>

        {daysQuery.isLoading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60"
              />
            ))}
          </div>
        )}

        {!daysQuery.isLoading && days.length === 0 && (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-10 text-center">
            <Calendar className="h-8 w-8 text-zinc-300" strokeWidth={1.5} aria-hidden />
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">No days yet</h3>
            <p className="max-w-md text-xs leading-relaxed text-zinc-500">
              Go back to <strong>Configure</strong> and click{' '}
              <strong>Generate day-by-day plan</strong> to build the trip. Each day holds the
              hotels, flights, activities and transfers the traveller experiences.
            </p>
          </div>
        )}

        {days.map((day, i) => {
          const dayComponents = byDay.map.get(day.id) ?? [];
          const date = formatDayDate(day.date);
          const prev = i > 0 ? days[i - 1] : undefined;
          const cityChanged = Boolean(
            prev &&
            day.destinationCityName &&
            prev.destinationCityName &&
            day.destinationCityName !== prev.destinationCityName,
          );
          const dayFlight = dayComponents.find((c) => c.kind === 'FLIGHT');
          // Resolve the stay this day belongs to by DATE, not by city name - a
          // city can repeat in the route (e.g. back to the same city after a
          // cruise), and city-change detection would miss the second stay's
          // first day, leaving it with no hotel placeholder. Falls back to the
          // city-change heuristic for legacy legs without resolved stay dates.
          const dayLeg = legForDate(detail?.legs, day.date);
          const isArrivalDay = i === 0 || (dayLeg ? day.date === dayLeg.checkIn : cityChanged);
          const prevCityId = prev?.destinationCityId ?? null;
          // A flight sits on this day when a configured flight leg lands here
          // (matched by its departure date). The leg is the single source of
          // truth for the route + date searched on TBO.
          const assignedLeg = flightLegByDayId.get(day.id) ?? null;
          const showFlight = Boolean(assignedLeg) && flightsEnabled;
          // Search context = the leg's exact from -> to -> date (fallback to the
          // day's city for a legacy flight with no configured leg).
          const flightFromCityId =
            assignedLeg?.fromCityId ?? prevCityId ?? detail?.fromCityId ?? null;
          const flightToCityId = assignedLeg?.toCityId ?? day.destinationCityId ?? null;
          const flightFromName = assignedLeg?.fromCityName ?? prev?.destinationCityName ?? null;
          const flightToName = assignedLeg?.toCityName ?? day.destinationCityName ?? null;
          const flightDate = assignedLeg?.departureDate ?? day.date;
          const slotted = dayComponents.filter(
            (c) => c.kind === 'ACTIVITY' || c.kind === 'TRANSFER' || c.kind === 'VAS',
          );
          // (Previously: arrival / departure HH:mm were computed from the flight's
          // first / last segment to gate the morning/afternoon/evening slot
          // buttons. The slot grouping is gone; activities and internal transfers
          // are added freely from the new flat list, so we no longer derive those
          // times here. The flight card still carries its own segments.)

          // Scaffold placeholders for what the generated plan expects on this day
          // but the agent hasn't filled yet. Structure-driven (from the flight +
          // hotel configuration), NOT from live search - the agent fills each
          // from our pickers / DB. Each disappears once its component is added.
          const actualFlight = dayFlight ?? null;
          // A block flight (from the FD Configure step) is fixed on the day plan:
          // no "Change flight" / remove here - it is edited on Configure. Any
          // extra block flights that land on the same day render read-only after.
          const flightIsBlock = actualFlight ? isFdBlockFlight(actualFlight) : false;
          const extraBlockFlights = dayComponents.filter(
            (c) => c.kind === 'FLIGHT' && c.id !== actualFlight?.id && isFdBlockFlight(c),
          );
          const actualHotel = dayComponents.find((c) => c.kind === 'HOTEL') ?? null;
          // Cruise-as-stay (Phase 2). The CRUISE component's dayId is its embark
          // day (same convention as the hotel's arrival-day anchor) - it renders
          // its full stay card there. On the nights strictly between embark and
          // disembark (no component of their own), show a lightweight "Aboard
          // <ship>" marker instead of leaving the day looking empty.
          const actualCruise = dayComponents.find((c) => c.kind === 'CRUISE') ?? null;
          const activeCruiseNight = !actualCruise
            ? (components.find((c) => {
                if (c.kind !== 'CRUISE') return false;
                if (!c.startDate || !c.endDate || !day.date) return false;
                return c.startDate < day.date && day.date < c.endDate;
              }) ?? null)
            : null;
          // Cruise-STAY day (derived from `detail.cruiseStays`, not from the
          // placed component): the day's date falls within some cruise stay's
          // [embarkDate, embarkDate + nights) range. Unlike `actualCruise` /
          // `activeCruiseNight` above (which only see a cruise stay if a
          // CRUISE component is still placed for it), this stays true even
          // after the operator removes the component - so the day never
          // mistakenly falls back to expecting a hotel, and the embark day
          // can offer an "Add cruise" placeholder instead.
          const dayCruiseStay = (() => {
            if (!day.date) return null;
            const d = new Date(`${day.date}T00:00:00Z`).getTime();
            return (
              (detail?.cruiseStays ?? []).find((cs) => {
                if (!cs.embarkDate) return false;
                const start = new Date(`${cs.embarkDate}T00:00:00Z`).getTime();
                const end = start + cs.nights * 86_400_000;
                return d >= start && d < end;
              }) ?? null
            );
          })();
          const isCruiseEmbarkDay = Boolean(dayCruiseStay && day.date === dayCruiseStay.embarkDate);
          // Nights for THIS stay from the date-resolved leg (so a repeat-city
          // stay shows its own night count + drives the hotel search length);
          // fall back to the first city-matching leg only when dates are absent.
          const legNights =
            dayLeg?.nights ?? detail?.legs?.find((l) => l.cityId === day.destinationCityId)?.nights;
          // Structural transfers carry a metaJson.slot marker so they render in
          // position (between flight and hotel, or as the city-to-city link)
          // rather than in the time-banded activity slots.
          const slotOf = (c: ItineraryComponentOut): string | null =>
            c.metaJson && typeof c.metaJson === 'object'
              ? (((c.metaJson as Record<string, unknown>).slot as string | undefined) ?? null)
              : null;
          const a2hTransfer =
            dayComponents.find((c) => c.kind === 'TRANSFER' && slotOf(c) === 'a2h') ?? null;
          const h2aTransfer =
            dayComponents.find((c) => c.kind === 'TRANSFER' && slotOf(c) === 'h2a') ?? null;
          const c2cTransfer =
            dayComponents.find((c) => c.kind === 'TRANSFER' && slotOf(c) === 'c2c') ?? null;
          const structuralIds = new Set(
            [a2hTransfer, h2aTransfer, c2cTransfer]
              .filter((c): c is ItineraryComponentOut => c !== null)
              .map((c) => c.id),
          );
          const daySlots = slotted
            .filter((c) => !structuralIds.has(c.id))
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder);

          const hotelExpected = isArrivalDay && Boolean(day.destinationCityId) && hotelsEnabled;
          const showFlightPlaceholder = canEdit && showFlight && !actualFlight;
          // A day covered by a cruise (embark day carries the cruise card, a
          // continuation night shows the "Aboard <ship>" marker) never needs
          // the hotel "Add hotel" placeholder - you sleep on the ship, not in
          // a hotel that night. Gated on `dayCruiseStay` (the STAY, not the
          // component) so this holds even after the CRUISE component is
          // removed - otherwise a removed cruise made its embark day fall
          // back to showing "Hotel / Add hotel", which is wrong: the day is
          // still a cruise night, just missing its card.
          const showHotelPlaceholder =
            canEdit &&
            hotelExpected &&
            !actualHotel &&
            !actualCruise &&
            !activeCruiseNight &&
            !dayCruiseStay;
          // A flight leaving this day = return / onward hop from the current
          // stay city. Two entry points:
          //  - Configured leg on a non-arrival day (`showFlight` from the leg
          //    resolver). Covers the return-home leg + any departure-by-flight
          //    day, so the hotel-to-airport placeholder appears BEFORE the
          //    operator has actually picked the flight.
          //  - Legacy: a flight component saved without a matching leg on a
          //    non-arrival day.
          const isReturnFlight = (Boolean(actualFlight) || showFlight) && !isArrivalDay;

          // Render a stay row as the real card or, if missing, a dashed placeholder.
          const flightEl = actualFlight ? (
            <ComponentCard
              key={actualFlight.id}
              component={actualFlight}
              editable={canEdit && !flightIsBlock}
              onEdit={() => {
                // Round-trip bundle vs one-way is decided SERVER-SIDE from the
                // legs (useItineraryFlightPlan), not a stored per-component
                // flag. An international return trip always opens the joint
                // round-trip picker for its outbound/return legs (fixes a leg
                // being re-picked one-way and breaking the bundle).
                if (
                  tryOpenFlightBundle(day.id, {
                    fromIata: actualFlight.fromLocation,
                    toIata: actualFlight.toLocation,
                  })
                ) {
                  return;
                }
                // Open the search popup to swap this one-way flight. The route +
                // date come from the configured flight leg for this day.
                setTboDrawer({
                  kind: 'flight',
                  dayId: day.id,
                  cityId: isReturnFlight ? (detail?.fromCityId ?? null) : flightToCityId,
                  cityName: isReturnFlight ? null : flightToName,
                  dayDate: actualFlight.startDate ?? flightDate,
                  originCityId: isReturnFlight ? day.destinationCityId : flightFromCityId,
                  // Route the picker prefills from - the stored IATA on the leg
                  // being changed (avoids a city-id -> getCity resolve).
                  originIata: actualFlight.fromLocation ?? null,
                  destIata: actualFlight.toLocation ?? null,
                  replaceId: actualFlight.id,
                  currentResultIndex:
                    (actualFlight.metaJson as { resultIndex?: string } | null)?.resultIndex ?? null,
                  currentCostPaise: actualFlight.costPaise,
                });
              }}
              onRemove={() => removeComponent(actualFlight)}
              onUpdate={(patch) => updateLocal(actualFlight.id, patch)}
              itineraryId={itineraryId}
              fromCityName={flightFromName}
              toCityName={flightToName}
            />
          ) : showFlightPlaceholder ? (
            <ScaffoldPlaceholder
              key="ph-flight"
              Icon={Plane}
              kind="Flight"
              label={`${flightFromName ?? 'Departure'} - ${flightToName ?? ''}`}
              cta="Add flight"
              onAdd={() => {
                // Same server-authoritative rule as editing: an international
                // return trip adds its outbound/return legs as one round-trip
                // bundle, not two one-ways.
                if (
                  tryOpenFlightBundle(day.id, {
                    fromCityId: flightFromCityId,
                    toCityId: flightToCityId,
                  })
                ) {
                  return;
                }
                setTboDrawer({
                  kind: 'flight',
                  dayId: day.id,
                  cityId: flightToCityId,
                  cityName: flightToName,
                  dayDate: flightDate,
                  originCityId: flightFromCityId,
                });
              }}
            />
          ) : null;

          // This stay's hotel source: the leg's per-city override when set, else
          // the itinerary's global source. Decides Live (TBO) vs Preferred picker.
          const stayHotelSrc = stayHotelSource(detail, day.destinationCityId, day.date);

          const hotelCardEl = actualHotel ? (
            <ComponentCard
              key={actualHotel.id}
              component={actualHotel}
              // A fixed-departure hotel comes from the template and is fixed -
              // no "Change hotel" / "Change Rooms & Meals" / remove. Flights stay
              // editable (they're the agent's).
              editable={canEdit && !structureLocked}
              onEdit={() =>
                stayHotelSrc === 'PREFERRED'
                  ? setPrefHotelDrawer({
                      dayId: day.id,
                      cityId: day.destinationCityId,
                      cityName: day.destinationCityName,
                      dayDate: day.date,
                      legNights: legNights ?? null,
                      replaceId: actualHotel.id,
                    })
                  : setTboDrawer({
                      kind: 'hotel',
                      dayId: day.id,
                      cityId: day.destinationCityId,
                      cityName: day.destinationCityName,
                      dayDate: day.date,
                      originCityId: null,
                      replaceId: actualHotel.id,
                      currentResultIndex:
                        (actualHotel.metaJson as { resultIndex?: string } | null)?.resultIndex ??
                        null,
                      currentCostPaise: actualHotel.costPaise,
                    })
              }
              onRemove={() => removeComponent(actualHotel)}
              onUpdate={(patch) => updateLocal(actualHotel.id, patch)}
              itineraryId={itineraryId}
              stayCityId={day.destinationCityId}
            />
          ) : showHotelPlaceholder && !structureLocked ? (
            <ScaffoldPlaceholder
              key="ph-hotel"
              Icon={BedDouble}
              kind="Hotel"
              label={`${day.destinationCityName ?? 'Stay'}${legNights ? ` · ${legNights} night${legNights === 1 ? '' : 's'}` : ''}`}
              cta="Add hotel"
              onAdd={() =>
                stayHotelSrc === 'PREFERRED'
                  ? setPrefHotelDrawer({
                      dayId: day.id,
                      cityId: day.destinationCityId,
                      cityName: day.destinationCityName,
                      dayDate: day.date,
                      legNights: legNights ?? null,
                    })
                  : setTboDrawer({
                      kind: 'hotel',
                      dayId: day.id,
                      cityId: day.destinationCityId,
                      cityName: day.destinationCityName,
                      dayDate: day.date,
                      originCityId: null,
                    })
              }
            />
          ) : null;

          // A TBO stay can carry a per-stay room override (e.g. 2 rooms x 2
          // adults when the global config is 1 room x 4 adults, which some TBO
          // hotels require). The control drives this stay's search + re-price;
          // other stays keep the itinerary's global rooms.
          const hotelEl = hotelCardEl ? (
            <div key={`hotel-slot-${day.id}`}>
              {stayHotelSrc === 'TBO' ? (
                <StayRoomsControl
                  detail={detail}
                  itineraryId={itineraryId}
                  cityId={day.destinationCityId}
                  date={day.date}
                  canEdit={canEdit && !structureLocked}
                />
              ) : null}
              {hotelCardEl}
            </div>
          ) : null;

          const transferRow = (
            slotKey: 'a2h' | 'h2a' | 'c2c',
            actual: ItineraryComponentOut | null,
            kindLabel: string,
            label: string,
            category: TransferCategory,
            cityId: string | null,
            cityName: string | null,
          ): React.ReactNode =>
            actual ? (
              <ComponentCard
                key={actual.id}
                component={actual}
                editable={canEdit}
                // Long-haul: structural transfers land at cost 0 (manual pricing)
                // just like free transfers, so they need the same inline net-price
                // editor - otherwise the price is neither shown nor editable.
                isLongHaul={detail?.isLongHaul ?? false}
                onManualPriceSave={handleManualPriceSave}
                onEdit={() =>
                  // 2026-07-07. Edit a structural transfer via the picker (not
                  // the generic component drawer) so operators pick a different
                  // TRANSFER template from the same catalog they used to add it.
                  // replaceId tells the picker to swap in-place.
                  setItemPicker({
                    dayId: day.id,
                    cityId,
                    cityName,
                    defaultTime: '09:00',
                    initialTab: 'TRANSFER',
                    initialCategory: category,
                    slot: slotKey,
                    replaceId: actual.id,
                    currentCostPaise: actual.costPaise,
                  })
                }
                onRemove={() => removeComponent(actual)}
                onUpdate={(patch) => updateLocal(actual.id, patch)}
                itineraryId={itineraryId}
                dayDate={day.date}
              />
            ) : canEdit && !structureLocked ? (
              // A fixed departure includes transfers in the land package, so we
              // never scaffold "Add transfer" placeholders for it - only flights.
              <ScaffoldPlaceholder
                key={`ph-${slotKey}`}
                Icon={ArrowRightLeft}
                kind={kindLabel}
                label={label}
                cta="Add transfer"
                onAdd={() =>
                  setItemPicker({
                    dayId: day.id,
                    cityId,
                    cityName,
                    defaultTime: '09:00',
                    initialTab: 'TRANSFER',
                    initialCategory: category,
                    slot: slotKey,
                  })
                }
              />
            ) : null;

          // 2026-07-07. Round-trip a2h coverage: if the arrival day for THIS
          // city already carries a `ROUND_TRIP` airport transfer, the return
          // leg is already booked. On the departure/return-flight day we skip
          // the empty "Add hotel-to-airport" placeholder and render a linked
          // reference card above the flight instead, so the operator can see
          // the coverage without accidentally adding a second transfer.
          // A ROUND_TRIP airport transfer booked on a city's arrival day already
          // covers that city's departure hop, so a later hotel->airport slot for
          // the same city shouldn't scaffold an empty "add" placeholder.
          const roundTripA2HFor = (cityId: string | null): ItineraryComponentOut | null => {
            if (!cityId) return null;
            for (const otherDay of days) {
              if (otherDay.id === day.id) continue;
              if (otherDay.destinationCityId !== cityId) continue;
              const comps = byDay.map.get(otherDay.id) ?? [];
              const rt = comps.find((c) => {
                if (c.kind !== 'TRANSFER') return false;
                const m = (c.metaJson ?? {}) as Record<string, unknown>;
                return m.slot === 'a2h' && m.transferType === 'ROUND_TRIP';
              });
              if (rt) return rt;
            }
            return null;
          };
          const roundTripA2H: ItineraryComponentOut | null =
            isReturnFlight && !h2aTransfer && day.destinationCityId
              ? roundTripA2HFor(day.destinationCityId)
              : null;
          // On a city-change-BY-FLIGHT day, the traveller first leaves the
          // PREVIOUS city's hotel for its airport - so that city (not the day's
          // arrival city) owns the hotel->airport transfer here.
          const depRoundTripA2H: ItineraryComponentOut | null =
            cityChanged && !h2aTransfer && prevCityId ? roundTripA2HFor(prevCityId) : null;

          // Logical order per day.
          const stayRows: React.ReactNode[] = [];
          if (isReturnFlight) {
            // Leaving the last city: hotel -> airport, then the flight home.
            // The hotel->airport transfer is hotel-dependent: skip it (and its
            // round-trip reference card) when hotel booking is off; the flight
            // home still shows.
            if (hotelsEnabled) {
              if (roundTripA2H) {
                // Round-trip a2h transfer covers this leg - render a reference
                // card above the flight instead of the empty h2a placeholder.
                stayRows.push(
                  <RoundTripReturnCard
                    key={`rt-return-${roundTripA2H.id}`}
                    transfer={roundTripA2H}
                    cityName={day.destinationCityName ?? null}
                  />,
                );
              } else {
                stayRows.push(
                  transferRow(
                    'h2a',
                    h2aTransfer,
                    'Hotel to airport',
                    `${day.destinationCityName ?? 'Hotel'} - airport`,
                    'AIRPORT',
                    day.destinationCityId,
                    day.destinationCityName,
                  ),
                );
              }
            }
            stayRows.push(flightEl);
          } else if (showFlight || (actualFlight && isArrivalDay)) {
            // Arrive by air: [prev city hotel -> airport], flight, airport ->
            // hotel (always show placeholder), hotel.
            // On a CITY-CHANGE day the traveller leaves the PREVIOUS city's hotel
            // for its airport before flying on, so lead with that hotel->airport
            // transfer FOR THE PREVIOUS CITY (day 1 arrives from the origin with
            // no prior hotel, so it's skipped). A round-trip airport transfer
            // already covering the previous city shows a reference card instead.
            if (cityChanged && prevCityId && hotelsEnabled) {
              if (depRoundTripA2H) {
                stayRows.push(
                  <RoundTripReturnCard
                    key={`rt-dep-${depRoundTripA2H.id}`}
                    transfer={depRoundTripA2H}
                    cityName={prev?.destinationCityName ?? null}
                  />,
                );
              } else {
                stayRows.push(
                  transferRow(
                    'h2a',
                    h2aTransfer,
                    'Hotel to airport',
                    `${prev?.destinationCityName ?? 'Hotel'} - airport`,
                    'AIRPORT',
                    prevCityId,
                    prev?.destinationCityName ?? null,
                  ),
                );
              }
            }
            // 2026-07-07. Always render the airport-to-hotel transfer slot when a
            // flight is shown - previously it only appeared once both the flight
            // and hotel were saved, so operators had to add both before they
            // could add the airport transfer.
            stayRows.push(flightEl);
            if (hotelExpected) {
              stayRows.push(
                transferRow(
                  'a2h',
                  a2hTransfer,
                  'Airport to hotel',
                  `Airport - ${day.destinationCityName ?? 'hotel'}`,
                  'AIRPORT',
                  day.destinationCityId,
                  day.destinationCityName,
                ),
              );
            }
            stayRows.push(hotelEl);
          } else if (cityChanged && isArrivalDay) {
            // Overland city change: city-to-city transfer, then hotel.
            stayRows.push(
              transferRow(
                'c2c',
                c2cTransfer,
                'City-to-city transfer',
                `${prev?.destinationCityName ?? ''} - ${day.destinationCityName ?? ''}`,
                'INTERCITY',
                prevCityId,
                prev?.destinationCityName ?? null,
              ),
            );
            stayRows.push(hotelEl);
          } else if (hotelExpected) {
            stayRows.push(hotelEl);
          }
          // A hotel saved on a day the structure didn't expect one (e.g. added
          // via the day's "Hotel" button on a continuation day) must still
          // render - otherwise it saves but never shows on the board.
          if (actualHotel && !stayRows.includes(hotelEl)) stayRows.push(hotelEl);
          // Cruise-as-stay (Phase 2): render the placed CRUISE component on its
          // embark day, same treatment as the hotel above - a full card,
          // independent of the flight/hotel structural branching (a cruise leg
          // can sit on any day, arrival or not).
          if (actualCruise) {
            const cruiseMeta = actualCruise.metaJson as {
              nights?: number;
              checkIn?: string;
            } | null;
            const cruiseCheckIn = actualCruise.startDate ?? cruiseMeta?.checkIn ?? null;
            const cruiseNights =
              cruiseMeta?.nights ??
              (cruiseCheckIn && actualCruise.endDate
                ? Math.max(
                    1,
                    Math.round(
                      (new Date(actualCruise.endDate + 'T00:00:00Z').getTime() -
                        new Date(cruiseCheckIn + 'T00:00:00Z').getTime()) /
                        86_400_000,
                    ),
                  )
                : 1);
            stayRows.push(
              <ComponentCard
                key={actualCruise.id}
                component={actualCruise}
                editable={canEdit && !structureLocked}
                isLongHaul={detail?.isLongHaul ?? false}
                onManualPriceSave={handleManualPriceSave}
                // "Change cruise" - full cruise list for the embark city.
                onEdit={() =>
                  setCruiseDrawer({
                    dayId: day.id,
                    cityId: day.destinationCityId,
                    cityName: day.destinationCityName,
                    checkIn: cruiseCheckIn,
                    nights: cruiseNights,
                    currency: detail?.currencyCode ?? 'INR',
                    replaceId: actualCruise.id,
                    lockedCruiseId: null,
                  })
                }
                // "Change cabin & meals" - locked to the cruise already on
                // the day, cabins only.
                onChangeCabin={() =>
                  setCruiseDrawer({
                    dayId: day.id,
                    cityId: day.destinationCityId,
                    cityName: day.destinationCityName,
                    checkIn: cruiseCheckIn,
                    nights: cruiseNights,
                    currency: detail?.currencyCode ?? 'INR',
                    replaceId: actualCruise.id,
                    lockedCruiseId: actualCruise.cruiseId,
                  })
                }
                onRemove={() => removeComponent(actualCruise)}
                onUpdate={(patch) => updateLocal(actualCruise.id, patch)}
                itineraryId={itineraryId}
              />,
            );
          } else if (
            isCruiseEmbarkDay &&
            cruiseEnabled &&
            canEdit &&
            !structureLocked &&
            dayCruiseStay
          ) {
            // The cruise stay's embark day, but its CRUISE component was
            // removed (the stay itself - `detail.cruiseStays` - is untouched).
            // Mirrors the hotel scaffold placeholder exactly: same treatment,
            // cruise icon/copy, opens the same "Change cruise" picker used to
            // add one back (`lockedCruiseId: null` = full list for the city).
            stayRows.push(
              <ScaffoldPlaceholder
                key="ph-cruise"
                Icon={Ship}
                kind="Cruise"
                label={`${day.destinationCityName ?? 'Stay'} · ${dayCruiseStay.nights} night${dayCruiseStay.nights === 1 ? '' : 's'}`}
                cta="Add cruise"
                onAdd={() =>
                  setCruiseDrawer({
                    dayId: day.id,
                    cityId: day.destinationCityId,
                    cityName: day.destinationCityName,
                    checkIn: dayCruiseStay.embarkDate,
                    nights: dayCruiseStay.nights,
                    currency: detail?.currencyCode ?? 'INR',
                    lockedCruiseId: null,
                  })
                }
              />,
            );
          }
          // A night strictly between check-in and check-out has no component of
          // its own: the cruise stay (like a hotel stay) is shown once on its
          // check-in day, so continuation nights render nothing extra. We still
          // suppress the hotel placeholder on those nights (see showHotelPlaceholder).
          // More than one block flight can land on the same day (e.g. a same-day
          // connection). The first fills the flight slot above; render the rest
          // as read-only cards so none are silently dropped.
          for (const bf of extraBlockFlights) {
            stayRows.push(
              <ComponentCard
                key={bf.id}
                component={bf}
                editable={false}
                onEdit={() => {}}
                onRemove={() => {}}
                onUpdate={() => {}}
                itineraryId={itineraryId}
              />,
            );
          }
          const stayRowsFiltered = stayRows.filter(Boolean);
          return (
            <React.Fragment key={day.id}>
              {cityChanged && prev && (
                <TravelCard
                  fromCity={prev.destinationCityName as string}
                  toCity={day.destinationCityName as string}
                  date={date}
                  flight={dayFlight}
                  transfer={c2cTransfer}
                />
              )}
              <section className="rounded-lg border border-zinc-200 bg-white">
                <header className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-3.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-zinc-900 px-1.5 text-xs font-semibold text-white tabular-nums">
                        {day.dayIndex}
                      </span>
                      <h3 className="truncate text-sm font-semibold tracking-tight text-zinc-900">
                        {day.title ?? `Day ${day.dayIndex}`}
                      </h3>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500">
                      {date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-zinc-400" aria-hidden />
                          {date}
                        </span>
                      )}
                      {day.destinationCityName && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-zinc-400" aria-hidden />
                          {day.destinationCityName}
                        </span>
                      )}
                      {day.mealPlan && <span>{day.mealPlan}</span>}
                    </div>
                  </div>
                  {/* Day metadata is not editable inline - to change a day's
                      title, city, meal plan etc. the operator goes back to
                      the Configure step and regenerates the plan. Only the
                      soft-delete action stays here - and not at all on a fixed
                      departure, whose day count is contractual. */}
                  {canEdit && !structureLocked && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() => handleDelete(day)}
                        disabled={pending}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        aria-label={`Remove day ${day.dayIndex}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </header>

                <div className="px-5 py-4 max-sm:px-3">
                  {day.briefHtml && (
                    <p className="mb-3 text-sm leading-relaxed whitespace-pre-line text-zinc-600">
                      {day.briefHtml}
                    </p>
                  )}
                  {componentsQuery.isLoading && dayComponents.length === 0 ? (
                    <p className="text-xs text-zinc-400">Loading components...</p>
                  ) : (
                    <div className="space-y-3">
                      {stayRowsFiltered.length > 0 && (
                        <div className="space-y-2">{stayRowsFiltered}</div>
                      )}
                      <DaySlots
                        components={daySlots}
                        editable={canEdit}
                        structureLocked={structureLocked}
                        onAdd={(kind) =>
                          setItemPicker({
                            dayId: day.id,
                            cityId: day.destinationCityId,
                            cityName: day.destinationCityName,
                            defaultTime: '09:00',
                            initialTab: kind,
                          })
                        }
                        onEdit={(c) => {
                          // 2026-07-07. Route ACTIVITY + TRANSFER edits through
                          // the same catalog picker used to add them, so
                          // operators can swap templates in-place. The generic
                          // ComponentFormDrawer stays as the fallback for any
                          // other kind that may land here (VAS, etc.).
                          if (c.kind === 'ACTIVITY' || c.kind === 'TRANSFER') {
                            const cMeta = (c.metaJson ?? {}) as Record<string, unknown>;
                            const cat =
                              c.kind === 'TRANSFER' && typeof cMeta.transferCategory === 'string'
                                ? (cMeta.transferCategory as TransferCategory)
                                : undefined;
                            setItemPicker({
                              dayId: day.id,
                              cityId: day.destinationCityId,
                              cityName: day.destinationCityName,
                              defaultTime: '09:00',
                              initialTab: c.kind,
                              initialCategory: cat,
                              replaceId: c.id,
                              currentCostPaise: c.costPaise,
                              // Carry the structural slot (a2h/h2a/c2c) so an
                              // in-place swap re-stamps it - otherwise the
                              // update would wipe the marker and knock the
                              // transfer out of its structural slot.
                              slot: (slotOf(c) ?? undefined) as 'a2h' | 'h2a' | 'c2c' | undefined,
                            });
                            return;
                          }
                          setComponentDrawer({ mode: 'edit', dayId: day.id, component: c });
                        }}
                        onRemove={(c) => removeComponent(c)}
                        onReorder={(orderedIds) => reorderLocal(day.id, orderedIds)}
                        isLongHaul={detail?.isLongHaul ?? false}
                        onManualPriceSave={handleManualPriceSave}
                        dayDate={day.date}
                      />
                    </div>
                  )}
                  {/* 2026-07-07. Bottom-left "Add component / Hotel / Flight"
                      trio hidden per UX simplification (2026-07-07). Everything
                      the operator needs is now surfaced via the structural
                      placeholders (Add flight / Add hotel / Add transfer) and
                      the day-slot picker inside DaySlots. The state, handlers
                      and pickers still exist for the placeholder onAdd hooks
                      to open the same drawers. */}
                </div>
              </section>
            </React.Fragment>
          );
        })}

        {byDay.supplements.length > 0 && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="border-b border-zinc-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                Vehicle supplements
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                Selected on the Configure step - change or remove them there.
              </p>
            </header>
            <div className="px-5 py-4 max-sm:px-3">
              <div className="space-y-2">
                {byDay.supplements.map((c) => (
                  <ComponentCard
                    key={c.id}
                    component={c}
                    editable={false}
                    onEdit={() => {}}
                    onRemove={() => {}}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {byDay.unscheduled.length > 0 && (
          <section className="rounded-lg border border-zinc-200 bg-white">
            <header className="border-b border-zinc-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Unscheduled</h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                Components not yet assigned to a specific day.
              </p>
            </header>
            <div className="px-5 py-4 max-sm:px-3">
              <div className="space-y-2">
                {byDay.unscheduled.map((c) => (
                  <ComponentCard
                    key={c.id}
                    component={c}
                    editable={canEdit}
                    onEdit={() => setComponentDrawer({ mode: 'edit', dayId: null, component: c })}
                    onRemove={() => removeComponent(c)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {componentDrawer && (
          <ComponentFormDrawer
            mode={componentDrawer.mode}
            component={componentDrawer.component}
            pending={componentPending}
            onClose={() => setComponentDrawer(null)}
            onSubmit={submitComponent}
          />
        )}

        {tboDrawer && (
          <TboPickerModal
            kind={tboDrawer.kind}
            itineraryId={itineraryId}
            dayDate={tboDrawer.dayDate}
            cityId={tboDrawer.cityId}
            cityName={tboDrawer.cityName}
            originCityId={tboDrawer.originCityId}
            originIata={tboDrawer.originIata}
            destIata={tboDrawer.destIata}
            currentResultIndex={tboDrawer.currentResultIndex}
            currentCostPaise={tboDrawer.currentCostPaise}
            bundle={tboDrawer.bundle}
            pending={componentPending}
            onClose={() => setTboDrawer(null)}
            onSubmit={submitTboComponent}
            onSubmitBundle={submitTboBundle}
          />
        )}
        {prefHotelDrawer && (
          <PreferredHotelPickerModal
            itineraryId={itineraryId}
            cityId={prefHotelDrawer.cityId}
            cityName={prefHotelDrawer.cityName}
            dayId={prefHotelDrawer.dayId}
            dayDate={prefHotelDrawer.dayDate}
            legNights={prefHotelDrawer.legNights}
            replaceId={prefHotelDrawer.replaceId}
            pending={componentPending}
            onClose={() => setPrefHotelDrawer(null)}
            onSubmit={submitPrefHotel}
          />
        )}
        {cruiseDrawer && (
          <CruiseChangeModal
            itineraryId={itineraryId}
            cityId={cruiseDrawer.cityId}
            cityName={cruiseDrawer.cityName}
            dayId={cruiseDrawer.dayId}
            checkIn={cruiseDrawer.checkIn}
            nights={cruiseDrawer.nights}
            currency={cruiseDrawer.currency}
            replaceId={cruiseDrawer.replaceId}
            lockedCruiseId={cruiseDrawer.lockedCruiseId}
            pending={componentPending}
            onClose={() => setCruiseDrawer(null)}
            onSubmit={submitCruise}
          />
        )}
        {itemPicker && (
          <DayItemPickerModal
            cityId={itemPicker.cityId}
            cityName={itemPicker.cityName}
            defaultTime={itemPicker.defaultTime}
            initialTab={itemPicker.initialTab}
            initialCategory={itemPicker.initialCategory}
            transferSlot={itemPicker.slot}
            adults={detail?.adultsCount ?? 2}
            children={detail?.childrenCount ?? 0}
            childAges={(detail?.roomGroups ?? []).flatMap((g) =>
              g.rooms.flatMap((r) => r.childAges ?? []),
            )}
            busy={componentPending}
            manualPrice={detail?.isLongHaul ?? false}
            currentCostPaise={itemPicker.currentCostPaise}
            onClose={() => setItemPicker(null)}
            onPick={(payload) => {
              // "Change transfer"/"Change activity": swap the picked payload
              // onto the EXISTING component (keep its id) so the save diff
              // PATCHes it in place instead of delete+create. This makes an
              // orphan structurally impossible - the previous delete+add pair
              // left the old row behind whenever the delete half no-op'd (stale
              // draft closure / slot-drop), which is how duplicate transfers
              // accumulated. Mirrors how flights/hotels/cruises already swap.
              // Look the target up in the LIVE draftRef (not the render-captured
              // `draft`) so an autosave id-swap can't make the lookup miss.
              const existing = itemPicker.replaceId
                ? draftRef.current.find((c) => c.id === itemPicker.replaceId)
                : undefined;
              if (existing) {
                // Keep the component's position (sortOrder) so an in-place swap
                // doesn't reorder the day; the picker payload rarely carries one.
                replaceLocal(existing.id, {
                  ...payload,
                  sortOrder: payload.sortOrder ?? existing.sortOrder,
                });
              } else {
                addLocal(itemPicker.dayId, payload);
              }
            }}
          />
        )}

        {/* Autosave status is reported to the parent via `onSavingChange`
            and rendered as a "Saving..." pill in the itinerary top bar -
            no bottom sticky bar in this tab anymore. */}
      </div>
    </div>
  );
};

/**
 * Dashed scaffold card for a component the generated plan expects but the agent
 * hasn't filled yet (flight / hotel / transfer). Clicking opens the right
 * picker. Disappears once the real component is added.
 */
/**
 * 2026-07-07. Read-only reference card for the return leg of a round-trip
 * airport transfer. Rendered above the return flight on the departure day
 * when the arrival day already carries a `transferType: ROUND_TRIP` a2h
 * transfer for the same city. Editing / removal happens on the source
 * transfer card in the arrival day - this card only exists so operators can
 * see the coverage.
 */
const RoundTripReturnCard = ({
  transfer,
  cityName,
}: {
  transfer: ItineraryComponentOut;
  cityName: string | null;
}): React.JSX.Element => {
  const meta = (transfer.metaJson ?? {}) as Record<string, unknown>;
  const vehicle = typeof meta.vehicleTypeName === 'string' ? meta.vehicleTypeName : null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 px-3 py-2.5">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white text-emerald-600 ring-1 ring-emerald-200">
        <ArrowRightLeft className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-emerald-800">Return airport transfer</p>
        <p className="truncate text-xs text-emerald-700/80">
          Covered by the round-trip transfer on the arrival day
          {vehicle ? ` · ${vehicle}` : ''}
          {cityName ? ` · ${cityName}` : ''}
        </p>
      </div>
      <span className="shrink-0 rounded bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
        Round trip
      </span>
    </div>
  );
};

const ScaffoldPlaceholder = ({
  Icon,
  kind,
  label,
  cta,
  onAdd,
}: {
  Icon: typeof ArrowRightLeft;
  kind: string;
  label: string;
  cta: string;
  onAdd: () => void;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onAdd}
    className="hover:border-primary/40 hover:bg-accent/40 flex w-full items-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 px-3 py-2.5 text-left transition-colors"
  >
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white text-zinc-400 ring-1 ring-zinc-200">
      <Icon className="h-4 w-4" aria-hidden />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-zinc-700">{kind}</p>
      <p className="truncate text-xs text-zinc-400">{label}</p>
    </div>
    <span className="text-primary inline-flex items-center gap-1 text-xs font-medium">
      <Plus className="h-3 w-3" aria-hidden /> {cta}
    </span>
  </button>
);
